import refreshCart, { sectionsRendering } from './refreshCart';
import handleError from './handleError';
import interactionBlocker from './interactionBlocker';
import { getSectionsToRenderByAdd } from './cartSections';

/**
 * Function will replace a previous item with a new item in the Shopify cart
 * @param {*} form HTML form element
 */
export default async function (form) {
  // Prepare data: The data from a HTML form will be converted to FormData
  const preparedData = new FormData(form);

  // If the previous item is the same that's already in the cart, we don't need to do anything
  if (preparedData.get('previous-variant-id') === preparedData.get('id')) {
    // remove the quick view content
    document.querySelector('[data-quick-view-content]').innerHTML = '';
    return interactionBlocker();
  }

  // Final data
  const newItemFormData = new FormData();
  const oldItemFormData = new FormData();

  // Shopify AJAX needs a structure like updates[794864053][id], updates[794864053][quantity] for working properly
  // more info here: https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
  for (const [key, value] of preparedData) {
    if (key !== 'previous-variant-quantity' && key !== 'previous-variant-id') {
      newItemFormData.append(`items[0][${key}]`, value);
    }
  }

  // Add item quantity, it's optional in a FormData request payload for Shopify AJAX API
  // but we want to ancipate any breaking change from Shopify if it is required in the future
  newItemFormData.append(`items[0][quantity]`, 1);

  // Set zero on the previous item quantity
  oldItemFormData.append(`updates[${preparedData.get('previous-variant-id')}]`, 0);

  // Set sections rendering on the last cart update request
  let getSectionsToRender = getSectionsToRenderByAdd();
  newItemFormData.append('sections', getSectionsToRender.map((section) => section.section).join(','));

  // We will remove old item. We will then add new one and optionally change the quantity on new one if it is more than 1
  try {
    const updateItemResponse = await fetchFactory('/cart/update.js', oldItemFormData);
    // Check if the response was successful
    if (!updateItemResponse.ok) {
      const errorData = await updateItemResponse.json();
      const error = new Error('Network cart update response was not ok');
      error.details = errorData;
      throw error;
    }

    const addItemResponse = await fetchFactory('/cart/add.js', newItemFormData);
    // Check if the response was successful
    if (!addItemResponse.ok) {
      const errorData = await addItemResponse.json();
      const error = new Error('Network cart add response was not ok');
      error.details = errorData;
      throw error;
    }

    const addItemData = await addItemResponse.json();

    sectionsRendering(addItemData.sections, { cartItemKey: addItemData.items[0].key });
    // Interaction blocker: unblock cart
    interactionBlocker();

    // Defer this block of code because they are no user-visible tasks
    setTimeout(async () => {
      let cartData; // store cart data at the end of this cart edit item flow
      const requestedQuantity = Number(preparedData.get('previous-variant-quantity'));

      if (requestedQuantity > 1) {
        // Change the quantity of the new item if it is more than 1
        const changeItemFormData = {
          id: preparedData.get('id'),
          quantity: requestedQuantity,
          sections: getSectionsToRender.map((section) => section.section).join(','),
        };

        cartData = await changeQuantityOnNewItem(cartData, changeItemFormData, getSectionsToRender);
      } else {
        // Call GET "cart.js" for getting the current cart state afterwards we have added a new item
        // because of lack of cart data from POST "add.js"
        const responseGetCart = await fetch('/cart.js');
        // Check if the response was successful
        if (!responseGetCart.ok) {
          const errorData = await responseGetCart.json();
          const error = new Error('Network GET cart.js response in the cart edit item flow was not ok');
          error.details = errorData;
          throw error;
        }

        // Provide sections property to cart data which was gotten from POST "add.js" and they're already updated
        cartData = await responseGetCart.json();
        cartData.sections = addItemData.sections;
      }

      // remove the quick view content
      document.querySelector('[data-quick-view-content]').innerHTML = '';

      refreshCart(cartData);
    }, 0);
  } catch (error) {
    // Interaction blocker: unblock cart
    console.error(error);
    interactionBlocker();
    handleError(error);
  }
}

function getSectionInnerHTML(html, selector) {
  return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
}

// Provide a personalized fetch call according to endpoint and body parameters
function fetchFactory(endpoint, body) {
  return fetch(endpoint, {
    method: 'POST',
    headers: (typeof body === 'string' && { 'Content-Type': 'application/json' }) || {},
    body,
  });
}

export async function changeQuantityOnNewItem(cartData, requestedItem, sectionsToRender) {
  const changeItemResponse = await fetchFactory('/cart/change.js', JSON.stringify(requestedItem));
  // We're going to treat the 422 status code as a success because it does try to update the item quantity.
  // Just, if the requested quantity was more than available, it updates the quantity in the cart but it doesn't bring a success response.
  if (changeItemResponse.status === 422) {
    // We need to bring the cart sections again which are actually updated
    const refreshCartResponse = await fetch(
      `/cart?sections=${sectionsToRender.map((section) => section.section).join(',')}`,
    );
    // Check if the response was successful
    if (!refreshCartResponse.ok) {
      const errorData = await refreshCartResponse.json();
      const error = new Error('Network cart sections response was not ok');
      error.details = errorData;
      throw error;
    }

    const refreshedCartSections = await refreshCartResponse.json();

    // We need to bring the cart object under this fail scenario
    const responseGetCart = await fetch('/cart.js');
    // Check if the response was successful
    if (!responseGetCart.ok) {
      const errorData = await responseGetCart.json();
      const error = new Error('Network GET cart.js response under a fail /change.js was not ok');
      error.details = errorData;
      throw error;
    }

    // Provide cart data properly which was gotten once "change.js" failed. Furthermore they're already updated
    cartData = await responseGetCart.json();
    cartData.sections = refreshedCartSections;

    showNotificationDeferred(refreshedCartSections); // asynchronoysly show notification
    return cartData;
  }
  return await changeItemResponse.json();
}

/**
 * Function specically done for handling and show a notification if the requested quantity is more than available asynchronoysly
 * It was done exclusively for hadling cart-items sections.
 * @param {*} sections Sections were gotten from the GET /cart request
 */
function showNotificationDeferred(sections) {
  for (const element of Object.keys(sections)) {
    if (element.includes('cart-items')) {
      const temporaryDiv = document.createElement('div');
      const sectionText = sections[element]; // value

      temporaryDiv.innerHTML = getSectionInnerHTML(sectionText, '[data-cart]');
      const quantityAvailable = temporaryDiv.querySelector('.CartProduct__quantity>select').value;
      handleError(`Only ${quantityAvailable} available, updated item quantity`);

      break;
    }
  }
}
